import {
  animate,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BookmarkRecord } from 'fl-bookmarks';
import { FavouriteRecord } from 'fl-favourites';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { filter, takeUntil } from 'rxjs/operators';
import { ClevertapEvent } from '../analytics/clevertap/enum/clevertap.eventType';
import { ClevertapService } from '../analytics/clevertap/service/clevertap.service';
import { AppEventName } from '../analytics/newrelic/enums/EventName';
import { NewrelicService } from '../analytics/newrelic/services/newrelic.service';
import { NewrelicAppStore } from '../analytics/newrelic/store/newrelicApp.store';
import { DataService } from '../data.service';
import { LanguageService } from '../language.service';
import {
  hasEpisodeTypes,
  isEpisodeType,
} from '../shared/common/const/common.const';
import {
  IDetailResource,
  ResourceActionState,
} from '../shared/common/interfaces/common.interface';
import { ContentType } from '../shared/enums/contentType';
import { PlayerState } from '../shared/enums/player-states';
import { SubscriptionAction } from '../shared/enums/subscription';
import { AuthService } from '../shared/services/auth/auth.service';
import { Destroyable } from '../shared/services/destroyable';
import { DialogService } from '../shared/services/dialog.service';
import { NavigationLibraryService } from '../shared/services/navigation/navigation.service';
import { RouteHistoryService } from '../shared/services/router-history.service';
import { RoutingService } from '../shared/services/routing.service';
import { LocalStorageService } from '../shared/services/storage/local-storage.service';
import { SessionStorageService } from '../shared/services/storage/session-storage.service';
import { DetailAhaGoldPromptComponent } from '../shared/shared-components/aha-upgrade-prompts/detail/detail-aha-gold-prompt/detail-aha-gold-prompt.component';
import {
  PlayerEventType,
  SimplePlayerService,
} from '../shared/shared-components/simple-player/simple-player.service';
import { RentToWatchComponent } from '../shared/shared-components/tvod/rent-to-watch/rent-to-watch.component';
import { DetailPageModeService } from './services/detail-mode.service';
import { DetailPageService } from './services/detail-page.service';
import { PageType } from '../analytics/newrelic/enums/PageType';
import { removeSplashScreen } from '../shared/utils/browserUtils';

@Component({
  selector: 'app-detail-page',
  templateUrl: './detail-page.component.html',
  styleUrls: ['./detail-page.component.scss'],

  animations: [
    trigger('zoomOutPage', [
      transition(':enter', [
        style({ transform: 'scale(1.05)', opacity: 0 }),
        animate('400ms ease-out', style({ transform: 'scale(1)', opacity: 1 })),
      ]),
    ]),
    trigger('zoomStagger', [
      transition(':enter', [
        query(
          '.zoom-item',
          [
            style({ transform: 'scale(1.0)', opacity: 0 }),
            stagger(100, [
              animate(
                '300ms ease-out',
                style({ transform: 'scale(1)', opacity: 1 })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
    ]),
    trigger('overlayAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate(
          '300ms ease-out',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ transform: 'translateY(100%)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('containerAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-20px)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
    ]),
    trigger('episodesAnimation', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
    ]),
  ],
})
export class DetailPageComponent
  extends Destroyable
  implements OnInit, OnDestroy
{
  detailResource: IDetailResource = {};
  activeSeasonId: string;
  isAddIconHovered = false;
  showOverlay = false;
  isMovie = true;
  selectedIndex: number;
  tabs = this.getTranslatedTabs();
  seasonIndex = 0;
  bookmark?: BookmarkRecord | null;
  horizontalCarouselBookmark?: BookmarkRecord | null;
  resourceAction: ResourceActionState = null;
  director: string;
  producer: string;
  studio: string;
  favourite: FavouriteRecord | boolean;
  moreLikeThis: ContainerVm;
  mostPopular: ContainerVm;
  seasons: ResourceVm[];
  episodes: ContainerVm | null = null;
  selectedSeason: ResourceVm;
  watchPercent: number;
  episodePageNumber: number = 1;
  private episodePageSize: number = 20;
  lang = 'en';

  private isComponentVisible = false;
  private isBackClicked = false;
  private _lastHandleBackgroundEvent: boolean = false;
  private analyticsResource: ResourceVm = null;
  private isFromDeeplinkNavigation = false; // Track if this page was opened via deeplink

  constructor(
    private readonly detailPageService: DetailPageService,
    private readonly detailPageModeService: DetailPageModeService,
    private readonly dataService: DataService,
    private readonly navService: NavigationLibraryService,
    private readonly simplePlayerService: SimplePlayerService,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private routeHistoryService: RouteHistoryService,
    private readonly routingService: RoutingService,
    private readonly authService: AuthService,
    private readonly newrelic: NewrelicService,
    private readonly newrelicStore: NewrelicAppStore,
    private readonly clevertapService: ClevertapService,
    private readonly languageService: LanguageService,
    private readonly activatedRoute: ActivatedRoute
  ) {
    super();
  }

  isHovered = false;

  get resource() {
    return this.detailResource.resource;
  }

  get activeResource() {
    return this.detailResource.activeResource;
  }

  ngOnInit() {
    this.lang = this.languageService.currLang;
    this.dataService.emitVerticalMenuEvent({
      action: 'hide',
    });
    this.dataService.currentUrl = this.router.url;

    removeSplashScreen();
    if (this.dataService.getPendingDeeplinkUrl) {
      this.dataService.setPendingDeeplinkUrl = null;
      this.dataService.isFromDeeplink = true;
    }
    // Priority 1: Resolver-fetched resource (deeplink or detail-to-detail navigation)
    const resolvedResource = this.activatedRoute.snapshot.data?.detailData;
    if (resolvedResource) {
      this.detailResource.contentType =
        resolvedResource.contentType as ContentType;
      this.detailResource.resource = resolvedResource;
      this.analyticsResource = resolvedResource;
      this.setActiveResource(resolvedResource);
      // Update the BehaviorSubject so currentResourceValue is available
      // (e.g. for AppComponent's router.events → simplePlayerService.handleResource)
      this.dataService.selectedCardData = resolvedResource;
    } else if (this.dataService.detailResource) {
      // Priority 2: In-app navigation with pre-built detail resource (series/episode drill-down)
      this.detailResource = this.dataService.detailResource;
      this.analyticsResource = this.detailResource.resource;
    }
    // Priority 3: Use selectedCardData (normal navigation)
    else if (this.dataService.selectedCardData) {
      this.detailResource.contentType = this.dataService.selectedCardData
        .contentType as ContentType;
      this.detailResource.resource = this.dataService.selectedCardData;
      this.analyticsResource = this.dataService.selectedCardData;
      this.setActiveResource(this.detailResource.resource);
    }
    // No data available - navigate back to home
    else {
      this.router.navigate([this.dataService.homeTab || '']);
      return;
    }
    // Save the deeplink flag before it gets cleared
    this.isFromDeeplinkNavigation = this.dataService.isFromDeeplink;

    this.isMovie = !hasEpisodeTypes.includes(
      this.resource?.contentType as ContentType
    );
    const bookmarkContentId = hasEpisodeTypes.includes(
      this.resource.contentType as ContentType
    )
      ? this.resource.seriesId || this.resource.id
      : this.resource.id;
    this.detailPageService.getBookmark(bookmarkContentId).then((bookmark) => {
      if (bookmark?.offset > 0) {
        const isEpisode = isEpisodeType.includes(
          this.resource.contentType as ContentType
        );
        this.bookmark = isEpisode
          ? bookmark.episodeId === this.resource.id
            ? bookmark
            : null
          : bookmark;
        this.horizontalCarouselBookmark = bookmark;
      }
      if (!this.isMovie) {
        if (this.activeResource.episodeNumber > 100) {
          this.episodePageSize = 100;
          this.episodePageNumber = Math.ceil(
            this.activeResource.episodeNumber / 100
          );
        } else {
          this.episodePageSize =
            this.activeResource.episodeNumber > 20
              ? this.activeResource.episodeNumber
              : 20;
          this.episodePageNumber = 1;
        }
        this.detailPageService
          .fetchSeriesData(
            this.activeResource,
            this.bookmark,
            this.episodePageNumber,
            this.episodePageSize
          )
          .then((data) => {
            if (!data) {
              this.onBack(null);
            }
            this.seasons = data?.seasons;
            this.episodes = data?.episodes;
            this.activeSeasonId = data?.activeSeasonId;
            this.seasonIndex = data?.seasons?.findIndex(
              (it) => it.id === data?.activeSeasonId
            );
            const isEpisodeType = hasEpisodeTypes.includes(
              this.resource.contentType as ContentType
            );
            if (!data?.activeEpisode && isEpisodeType) {
              this.paginate(data?.seasons, true);
            } else {
              if (data?.activeEpisode && isEpisodeType) {
                this.setActiveResource(data.activeEpisode);
              }
              this.loadPageInfo();
            }
          });
      } else {
        this.loadPageInfo();
      }
    });

    this.detailPageService.fetchPageData(this.resource).then((res) => {
      this.favourite = res.favourite;
      if (res.moreLikeThis) {
        this.moreLikeThis = res.moreLikeThis;
      }
      if (res.mostPopular) {
        this.mostPopular = res.mostPopular;
      }
    });

    this.dataService.currentComponent
      .pipe(takeUntil(this.destroy$))
      .subscribe((component) => {
        this.isComponentVisible = component === DetailPageComponent;
      });

    this.setupAutoPlayInPlayer();
    this.newrelic.sendAppEvent(AppEventName.VIEW_CONTENT_DETAILS, {
      ...this.newrelicStore.getContentDetails(
        this.analyticsResource || this.resource,
        PageType.Details,
        Number(this.analyticsResource?.ex_id || this.resource?.ex_id)
      ),
    });
    this.clevertapService.addEvent(ClevertapEvent.CONTENT_CLICKED, {
      resource: this.analyticsResource || this.resource,
    });
  }

  ngAfterViewInit(): void {
    this.navService.focusContainer('button', true, 0, true);
  }

  getTranslatedTabs() {
    return [
      this.languageService.getTranslatedText('detail_episode_title'),
      this.languageService.getTranslatedText('cast_crew'),
      this.languageService.getTranslatedText('more_like_this'),
    ];
  }

  onBack(event: Event | null): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.showOverlay) {
      this.handleBackgroundPlay(false);
      this.showOverlay = false;
      this.selectedIndex = null;
      this.navService.focusContainer('button', true, 0, true);
      this.changeDetectorRef.detectChanges();
    } else {
      if (!this.isBackClicked) {
        this.isBackClicked = true;
        this.dataService.detailResource = undefined;
        this.dataService.shouldSkipResolver = true;

        // Check if navigation came from deeplink (use local flag)
        if (this.isFromDeeplinkNavigation) {
          this.dataService.isFromDeeplink = false; // Clear global flag
          // Navigate to home using router instead of full page reload
          this.router.navigate(['/']);
          return;
        }

        this.routeHistoryService.goBack();
        return;
      }
    }
  }

  private handleBackgroundPlay(isPause: boolean) {
    if (this._lastHandleBackgroundEvent !== isPause) {
      this._lastHandleBackgroundEvent = isPause;
      if (this.dataService.currentResourceValue?.apUrl?.dash) {
        this.simplePlayerService.updatePlayerState(
          isPause ? PlayerState.PAUSE : PlayerState.RESUME
        );
      }
    }
  }

  getTrailerLabel() {
    return this.languageService.getTranslatedText('trailer');
  }

  getStartOverLabel() {
    return this.languageService.getTranslatedText('playback.startover');
  }

  getSeasonLabel(seasonNumber: number) {
    return (
      this.languageService.getTranslatedText('player.season') +
      ' ' +
      seasonNumber
    );
  }

  loadPageInfo(isInitialLoad: boolean = true) {
    this.resourceAction = this.detailPageModeService.getResourceAction(
      this.activeResource,
      this.resource,
      this.bookmark
    );
    if (isInitialLoad) {
      this.navService.focusContainer('button', true, 0, true);

      if (this.dataService.forcePlayVideo) {
        this.dialogService.closeAll();
        this.dataService.forcePlayVideo = false;
        this.onPlayButtonClick();
      }
    }
    this.setBookmarkOffset();
    this.director = this.resource?.localizedDirector?.[0]?.localized?.find(
      (it) => it.languageCode === this.lang
    )?.localizeName;
    this.studio = this.resource?.studio[this.lang]?.[0];
  }

  setBookmarkOffset() {
    if (this.bookmark) {
      const bookmarkPercent = this.detailPageService.watchPercent(
        this.bookmark.offset,
        this.activeResource.offset
      );
      this.bookmark.offset = bookmarkPercent;
      this.watchPercent = bookmarkPercent;
    }

    if (!this.horizontalCarouselBookmark) return;
    if (
      this.bookmark?.episodeId === this.horizontalCarouselBookmark.episodeId
    ) {
      this.horizontalCarouselBookmark.offset = this.bookmark.offset;
    } else {
      const carouselPercent = this.detailPageService.watchPercent(
        this.horizontalCarouselBookmark.offset,
        this.activeResource.offset
      );
      this.horizontalCarouselBookmark.offset = carouselPercent;
    }
  }

  setActiveResource(resource: ResourceVm) {
    this.detailResource.activeResource = resource;
    this.dataService.detailResource = this.detailResource;
  }

  ngOnDestroy(): void {
    this.analyticsResource = null;
    this.cleanup();
    this.dataService.emitVerticalMenuEvent({
      action: 'show',
    });
  }

  showTvodHintIcon() {
    return this.resource.isTvod && !this.resourceAction.showTvodButton;
  }

  onBottomOverlayFocus(isFocused: boolean, index: number) {
    this.handleBackgroundPlay(isFocused);
    this.showOverlay = isFocused;
    this.selectedIndex = isFocused ? index : null;
    this.changeDetectorRef.detectChanges();
  }

  resetBottomOverlay() {
    this.onBottomOverlayFocus(false, null);
    this.navService.focusContainer('button', true, 0, true);
  }

  onSeasonFocus(index: number, season: ResourceVm) {
    if (this.seasonIndex === index) return;
    this.episodes = null;
    this.seasonIndex = index;
    this.episodePageNumber = 1;
    this.activeSeasonId = season.id;

    // Reset data-index to 0 for horizontal carousel cards
    const carouselCards = document.querySelector('#horizontal-carousel-cards');
    if (carouselCards) {
      carouselCards.setAttribute('data-index', '0');
    }

    this.detailPageService
      .fetchEpisodes(
        this.activeResource.seriesId,
        this.activeSeasonId,
        season.seasonNumber,
        this.episodePageNumber,
        this.episodePageSize
      )
      .then((it) => {
        this.episodes = it;
        this.changeDetectorRef.detectChanges();
      });
  }

  get title() {
    return this.resource.title[this.lang];
  }

  onAddIcon(isFocused: boolean) {
    this.isAddIconHovered = isFocused;
  }

  async updateFavourite() {
    const isFavourite = !Boolean(this.favourite);
    const result = await this.detailPageService.updateFavourite(
      this.resource.id,
      isFavourite
    );
    if (result !== null) {
      this.favourite = result;
    }
    this.newrelic.sendAppEvent(AppEventName.FAVORITE_CONTENT, {
      ...this.newrelicStore.getFavoriteContentInfo(
        this.resource,
        undefined,
        isFavourite,
        this.resource?.seasonNumber
          ? this.resource?.seasonNumber?.toString()
          : undefined,
        this.resource?.episodeNumber
          ? this.resource?.episodeNumber?.toString()
          : undefined
      ),
    });
    isFavourite
      ? this.clevertapService.addEvent(ClevertapEvent.ADD_WATCHLIST, {
          resource: this.activeResource,
        })
      : this.clevertapService.addEvent(ClevertapEvent.REMOVE_WATCHLIST, {
          resource: this.activeResource,
        });
  }

  paginate(seasons: ResourceVm[], isEntireData: boolean = false) {
    if (
      Math.ceil(this.episodePageNumber * this.episodePageSize) <
      this.episodes.totalItems
    ) {
      if (isEntireData) {
        this.episodePageNumber = Math.ceil(
          this.episodes.totalItems / this.episodePageSize
        );
      } else {
        this.episodePageNumber++;
      }
      this.detailPageService
        .fetchEpisodes(
          this.activeResource.seriesId || this.activeResource.id,
          this.activeSeasonId,
          seasons[this.seasonIndex].seasonNumber,
          isEntireData ? 1 : this.episodePageNumber,
          isEntireData ? 100 : this.episodePageSize
        )
        .then((it) => {
          if (isEntireData) {
            this.episodes = it;
          } else {
            this.episodes.resources.push(...it.resources);
          }
          if (this.authService.isLoggedIn()) {
            const activeEpisode = it.resources.find(
              (it) => it.id === this.bookmark?.episodeId
            );
            if (activeEpisode) {
              this.setActiveResource(activeEpisode);
            }
          }
        });
    }
  }

  async onPlayButtonClick() {
    if (this.resourceAction.showTvodButton) {
      const priceCode =
        this.resourceAction.catalogItem?.currencyCode +
        ' ' +
        this.resourceAction.catalogItem?.retailPrice;
      this.dialogService.openDialog(RentToWatchComponent, {
        data: {
          resource: this.activeResource,
          isTvod: true,
          priceCode,
        },
      });
    } else if (this.resourceAction?.playButtonText) {
      this.navigateToPlayer();
    } else if (this.resourceAction?.showSubscriptionActionButton) {
      if (
        this.resourceAction?.showSubscriptionActionButton ===
        SubscriptionAction.UPGRADE
      ) {
        // this.dialogService.openDialog(DetailAhaGoldDialogComponent, {
        //   data: {
        //     buttonText: 'go_home',
        //   },
        // });
        this.dataService.detailResource = null;
        this.dialogService.openDialog(DetailAhaGoldPromptComponent, {
          data: {
            buttonText: 'go_back',
          },
        });
      } else {
        this.router.navigate(['plans']);
      }
    }
  }

  onTrailerOrStartOverClick() {
    const { type, id: contentID } = this.activeResource;
    if (this.resourceAction?.showTrailerButton) {
      const trailers =
        (this.resource.trailers?.length && this.resource.trailers) ||
        (this.activeResource.trailers?.length && this.resource.trailers);
      const trailerId = trailers[0];
      const manualType = 'trailer';
      this.dataService.currentUrl = `player/${manualType}/${trailerId}`;
      this.dataService.previousUrl = this.router.url;
      this.router.navigate([`player/${manualType}/${trailerId}`], {
        state: {
          type: manualType,
          id: trailerId,
          analyticsResource: this.analyticsResource || this.activeResource,
        },
      });
    } else if (this.resourceAction?.showStartOverButton) {
      this.dataService.currentUrl = `player/${type}/${contentID}`;
      this.dataService.previousUrl = this.router.url;
      this.routingService.navigateToPlayerWithValidation({
        resource: this.activeResource,
        navigationPath: `player/${type}/${contentID}`,
        state: { isStartOver: true },
      });
    }
  }

  private setupAutoPlayInPlayer() {
    this.simplePlayerService.playerEvents$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.type === PlayerEventType.VIDEO_COMPLETED)
      )
      .subscribe(async (_) => {
        if (this.isComponentVisible && this.shouldAutoPlay()) {
          await this.navigateToPlayer();
        }
      });

    this.detailPageModeService.playerStateValue$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (this.showOverlay) return;
        this.simplePlayerService.updatePlayerState(state);
      });
  }

  private shouldAutoPlay(): boolean {
    return (
      !this.resourceAction?.showSubscriptionActionButton &&
      this.resourceAction?.showPlayButton &&
      !this.dialogService.hasOpenDialog()
    );
  }

  private async navigateToPlayer() {
    const { type, id: contentID } = this.activeResource;
    const playerUrl = `player/${type}/${contentID}`;

    this.dataService.currentUrl = playerUrl;
    this.dataService.previousUrl = this.router.url;
    await this.routingService.navigateToPlayerWithValidation({
      resource: this.activeResource,
      navigationPath: playerUrl,
      state: { analyticsResource: this.analyticsResource },
    });
  }

  onContainerFocus(verticalIndex: number) {
    this.navService.setVerticalIndex(verticalIndex);
  }

  trackByIndex(index: number): number {
    return index;
  }
}
